import { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { setAgentDisplayTitles } from "../../services/api";
import {
  DISPLAY_TITLE_OPTIONS,
  MAX_DISPLAY_TITLES,
  LICENSED_FIXED_LABEL,
  getAgentTitleValues,
  resolveTitleLabel,
} from "../../utils/agencyDisplayTitle";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "./v2";

// Curated display-title picker — V2 restyle 1:1 from ARTBOARD-SPEC board
// 2g (dropdown panel r11, 13.5px options, selected row bg #F5F9FF with a
// brand check, "No title" muted). Cosmetic only. Settable by the member
// on their own card (no account_id) or by an AgencyAdmin on any member
// (account_id) — wiring (setAgentDisplayTitles, optimistic update).
//
// F-5 follow-up: `autoOpen` lets the card's "Edit details" menu entry
// open THIS editor (the only existing member-detail edit surface) —
// entry point only, no new edit surface. Parent clears the flag via
// onAutoOpenConsumed.
//
// W1-06 (fix wave 1):
// - `licensed` members always show the fixed "Agent" title first. It is
//   system-derived from is_licensed (same signal as the Licensed pill),
//   not stored, and cannot be removed in the editor.
// - The dropdown is a MULTI-select (up to MAX_DISPLAY_TITLES titles per
//   person) over the completed roles list in DISPLAY_TITLE_OPTIONS.
// - A free CUSTOM title can be typed (max 60 chars, shown verbatim,
//   never translated). "No title" clears every stored title.

const CUSTOM_MAX_LENGTH = 60;

const DisplayTitleEditor = ({
  agent,
  editable,
  adminAccountId,
  onSaved,
  licensed = false,
  autoOpen = false,
  onAutoOpenConsumed,
}) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [optimistic, setOptimistic] = useState(undefined);
  const [customText, setCustomText] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (autoOpen && editable) {
      setOpen(true);
      onAutoOpenConsumed?.();
    }
  }, [autoOpen, editable, onAutoOpenConsumed]);

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  // Stored tokens (enum values and/or custom strings), optimistic during save.
  const values = optimistic !== undefined ? optimistic : getAgentTitleValues(agent);
  const isEnumValue = (v) => DISPLAY_TITLE_OPTIONS.some((o) => o.value === v);
  // One span PER label: the house text-matching translator works per text
  // node, so predefined labels must not be joined with custom text into a
  // single node. Predefined labels get statically translated; custom entries
  // carry data-no-translate so the dynamic pass leaves them verbatim (W1-06:
  // custom titles are never translated).
  let labelEntries = values.map((v) => ({
    label: resolveTitleLabel(v),
    custom: !isEnumValue(v),
  }));
  if (licensed) {
    labelEntries = [
      { label: LICENSED_FIXED_LABEL, custom: false },
      ...labelEntries.filter((e) => e.label !== LICENSED_FIXED_LABEL),
    ];
  }
  const labelNodes = labelEntries.length
    ? labelEntries.map((e, i) => (
        <span key={`${e.label}-${i}`}>
          {i > 0 && <span>, </span>}
          <span data-no-translate={e.custom ? "true" : undefined}>{e.label}</span>
        </span>
      ))
    : null;

  const save = async (nextValues) => {
    setSaving(true);
    setOptimistic(nextValues);
    try {
      const res = await setAgentDisplayTitles(nextValues, adminAccountId);
      if (res.success) {
        onSaved?.();
      } else {
        setOptimistic(undefined);
        toast.error("Couldn't update title");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggle = (value) => {
    if (!value) {
      // "No title" clears everything stored (the fixed licensed "Agent"
      // label is system-derived and stays).
      setOpen(false);
      save([]);
      return;
    }
    const has = values.includes(value);
    if (!has && values.length >= MAX_DISPLAY_TITLES) {
      toast.error(`Up to ${MAX_DISPLAY_TITLES} titles`);
      return;
    }
    save(has ? values.filter((v) => v !== value) : [...values, value]);
  };

  const addCustom = () => {
    const text = customText.trim();
    if (!text) return;
    if (text.length > CUSTOM_MAX_LENGTH) {
      toast.error(`Max ${CUSTOM_MAX_LENGTH} characters`);
      return;
    }
    const exists = values.some(
      (v) => v.toLowerCase() === text.toLowerCase() ||
        (resolveTitleLabel(v) || "").toLowerCase() === text.toLowerCase()
    );
    if (exists) {
      setCustomText("");
      return;
    }
    if (values.length >= MAX_DISPLAY_TITLES) {
      toast.error(`Up to ${MAX_DISPLAY_TITLES} titles`);
      return;
    }
    setCustomText("");
    save([...values, text]);
  };

  if (!editable) {
    return labelNodes ? (
      <span className="text-[12.5px] text-[#64748B] whitespace-nowrap overflow-hidden text-ellipsis">{labelNodes}</span>
    ) : null;
  }

  // Custom (non-enum) tokens get their own toggle rows above the input.
  const customValues = values.filter(
    (v) => !DISPLAY_TITLE_OPTIONS.some((o) => o.value === v)
  );
  // For licensed members the selectable "Agent" row is replaced by the fixed
  // row at the top, so hide it from the toggle list.
  const selectableOptions = DISPLAY_TITLE_OPTIONS.filter(
    (o) => !(licensed && o.value === "agent")
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        disabled={saving}
        className="inline-flex items-center gap-1 text-[12.5px] text-[#64748B] hover:text-[#1668D9] max-w-full"
      >
        <span className="whitespace-nowrap overflow-hidden text-ellipsis">{labelNodes || "Add a title"}</span>
        {open ? (
          <ChevronUpIcon size={13} stroke="#64748B" strokeWidth={2.2} />
        ) : (
          <ChevronDownIcon size={11} stroke="currentColor" strokeWidth={2.2} />
        )}
      </button>
      {open && (
        <div
          className="absolute z-30 left-0 top-full mt-1.5 w-[224px] bg-white border border-[#E4E9F0] rounded-[11px] shadow-[0_12px_32px_rgba(15,23,42,0.14)] p-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="max-h-[264px] overflow-y-auto">
            {licensed && (
              // W1-06 part 1: fixed, system-derived title for licensed
              // members — always on, not removable.
              <div className="w-full inline-flex items-center justify-between gap-2 text-left px-2.5 py-1.5 rounded-[7px] text-[13.5px] bg-[#F5F9FF] font-semibold text-[#0F172A] cursor-default">
                <span className="min-w-0 truncate">{LICENSED_FIXED_LABEL}</span>
                <CheckIcon size={13} strokeWidth={2.6} stroke="#2283FF" />
              </div>
            )}
            {selectableOptions.map((o) => {
              const active = o.value
                ? values.includes(o.value)
                : values.length === 0;
              return (
                <button
                  key={o.value || "none"}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className={`w-full inline-flex items-center justify-between gap-2 text-left px-2.5 py-1.5 rounded-[7px] text-[13.5px] ${
                    o.value ? "text-[#334155]" : "text-[#94A3B8]"
                  } ${active ? "bg-[#F5F9FF] font-semibold text-[#0F172A]" : "hover:bg-[#F5F8FC]"}`}
                >
                  <span className="min-w-0 truncate">{o.label}</span>
                  {active && <CheckIcon size={13} strokeWidth={2.6} stroke="#2283FF" />}
                </button>
              );
            })}
            {customValues.map((v) => (
              <button
                key={`custom-${v}`}
                type="button"
                onClick={() => toggle(v)}
                className="w-full inline-flex items-center justify-between gap-2 text-left px-2.5 py-1.5 rounded-[7px] text-[13.5px] bg-[#F5F9FF] font-semibold text-[#0F172A]"
              >
                <span className="min-w-0 truncate" data-no-translate="true">{v}</span>
                <CheckIcon size={13} strokeWidth={2.6} stroke="#2283FF" />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-[#EEF2F7] px-1">
            <input
              type="text"
              value={customText}
              maxLength={CUSTOM_MAX_LENGTH}
              placeholder="Custom title"
              onChange={(e) => setCustomText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustom();
                }
              }}
              className="min-w-0 flex-1 text-[13px] text-[#334155] placeholder-[#94A3B8] bg-transparent outline-none px-1.5 py-1"
            />
            <button
              type="button"
              onClick={addCustom}
              disabled={saving || !customText.trim()}
              className="text-[12.5px] font-semibold text-[#1668D9] disabled:text-[#B6C2D1] px-1.5 py-1"
            >
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayTitleEditor;
